// src/modules/leads/EmailModal.js
//
// Compose an email to a lead. Mirrors SmsModal, with one structural difference:
// the email body lives in Brevo, not here. A template selects which Brevo design
// to render; the textarea below supplies the custom text that template drops
// into its {{params.message}} block.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Modal from '../../components/shared/Modal';
import TextArea from '../../components/shared/TextArea';
import { FaSpinner, FaPaperPlane, FaEnvelope, FaExclamationTriangle } from 'react-icons/fa';
import { sendEmail, getLeadCommunicationHistory } from '../../services/communicationService';
import { getEmailTemplates } from '../../services/emailTemplateService';
import { replaceTemplateVariables } from '../follow-ups/utils/templateUtils';
import TemplateSelector from '../follow-ups/components/TemplateSelector';
import { getLeadWithRelated, getLeadEstimates } from './leadService';

const Recipient = styled.div`
  font-size: 1.05em;
  color: ${(props) => props.theme.colors.textPrimary};
  margin-bottom: 1em;
  display: flex;
  align-items: center;
  gap: 8px;
  word-break: break-all;

  svg {
    color: ${(props) => props.theme.colors.primary};
    flex-shrink: 0;
  }
`;

// Shown instead of <Recipient> when the lead has more than one address.
const RecipientPicker = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 1em;
`;

const RecipientLabel = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${(props) => props.theme.colors.textSecondary || '#6b7280'};
`;

const RecipientOption = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  color: ${(props) => props.theme.colors.textPrimary};
  background: ${(props) => (props.$active ? 'rgba(246, 150, 32, 0.08)' : 'transparent')};
  border: 1px solid ${(props) => (props.$active ? '#F69620' : (props.theme.colors.border || '#d1d5db'))};

  input[type='radio'] {
    accent-color: #F69620;
    cursor: pointer;
    flex-shrink: 0;
  }

  .recipient-text {
    word-break: break-all;
  }

  &:hover {
    border-color: #F69620;
  }
`;

const StatusMessage = styled.div`
  font-size: 14px;
  color: ${(props) => (props.error ? 'red' : 'green')};
  margin-bottom: 10px;
`;

// Shown when the lead has no email on file — the send controls stay disabled
// rather than failing at submit.
const BlockedNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: ${(props) => props.theme.borderRadius};
  background: ${(props) => props.theme.colors.error}22;
  border: 1px solid ${(props) => props.theme.colors.error}55;
  color: ${(props) => props.theme.colors.textPrimary};
  font-size: 14px;
  line-height: 1.4;

  svg {
    color: ${(props) => props.theme.colors.error};
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: ${(props) => props.theme.typography.fontWeight.medium};
  color: ${(props) => props.theme.colors.textSecondary};
  margin-bottom: 6px;
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${(props) => props.theme.colors.textSecondary};
  margin-top: 4px;
`;


const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: ${(props) => props.theme.borderRadius};
  background: ${(props) => props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textPrimary};
  border: 1px solid ${(props) => props.theme.colors.inputGray}66;
  font-size: 14px;
`;

const HistoryContainer = styled.div`
  max-height: 220px;
  overflow-y: auto;
  margin-bottom: 15px;
  padding: 5px 0;
  border-top: 1px solid ${(props) => props.theme.colors.inputGray}33;
  border-bottom: 1px solid ${(props) => props.theme.colors.inputGray}33;
`;

const HistoryItem = styled.div`
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.4;
  background-color: ${(props) => props.theme.colors.primary}22;
  word-wrap: break-word;
`;

const HistorySubject = styled.div`
  font-weight: ${(props) => props.theme.typography.fontWeight.bold};
  margin-bottom: 2px;
`;

const HistoryMeta = styled.div`
  font-size: 11px;
  color: ${(props) => props.theme.colors.textSecondary};
  text-align: right;
  margin-top: 4px;
`;

const Loading = styled.div`
  text-align: center;
  padding: 20px 0;
  color: ${(props) => props.theme.colors.textSecondary};

  .icon-spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to   { transform: rotate(360deg); }
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 18px;
`;

const Btn = styled.button`
  padding: ${(props) => props.theme.buttonSizes.small};
  border: none;
  border-radius: ${(props) => props.theme.borderRadius};
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${(props) =>
    props.variant === 'primary' ? props.theme.colors.primary : props.theme.colors.surface};
  color: ${(props) => props.theme.colors.textPrimary};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EmailModal = ({ isOpen, onClose, lead, userId }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [history, setHistory] = useState([]);
  // Lead enriched with the fields template variables need ({book_a_call_url},
  // {city}, {quote_url}…). Callers pass only id/name/email.
  const [templateLead, setTemplateLead] = useState(lead);

  // Which address this email goes to. Null until the rep picks, in which case
  // the lead's primary address is used.
  const [selectedRecipient, setSelectedRecipient] = useState('');

  // Every address on the lead, primary first. `emailAddresses` arrives with the
  // enrich fetch below; until then (and for callers that pass only id/name/email,
  // like LeadMapView) this is just the primary, i.e. today's behaviour.
  const recipientOptions = useMemo(() => {
    const rows = templateLead?.emailAddresses || [];
    const seen = new Set();
    const out = [];
    const push = (addr, label, isPrimary) => {
      const value = String(addr || '').trim();
      if (!value.includes('@')) return;
      const key = value.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ value, label, isPrimary });
    };
    push(lead?.email, 'Primary', true);
    rows.forEach((r) => push(r.email_address, r.label, r.is_primary));
    return out;
  }, [lead, templateLead]);

  // lead.email is nullable, and preferred_contact === 'email' does not
  // guarantee an address was ever captured.
  const recipient = (selectedRecipient || lead?.email || '').trim();
  const hasRecipient = recipient.includes('@');

  const loadTemplates = useCallback(async () => {
    try {
      const rows = await getEmailTemplates();
      setTemplates(rows || []);
    } catch (err) {
      setStatusMessage('Failed to load email templates');
      setIsError(true);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    if (!lead?.id) return;
    try {
      setIsLoading(true);
      const rows = await getLeadCommunicationHistory(lead.id);
      const emails = (rows || []).filter((item) => item.type === 'email');
      emails.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setHistory(emails);
    } catch (err) {
      console.error('Error fetching email history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [lead]);

  // Pull book_a_call_url / city / estimates by lead id so {quote_url} and
  // friends resolve in the default body. Callers pass only id/name/email, so we
  // fetch the rest here — same approach as SmsModal.enrichLeadForTemplates.
  const enrichLeadForTemplates = useCallback(async () => {
    if (!lead?.id) return;
    try {
      const [detailRes, estimatesRes] = await Promise.all([
        getLeadWithRelated(lead.id).catch(() => null),
        getLeadEstimates(lead.id).catch(() => null),
      ]);
      setTemplateLead((prev) => ({
        ...(detailRes?.data || {}),
        ...prev,
        book_a_call_url: prev?.book_a_call_url || detailRes?.data?.book_a_call_url,
        city: prev?.city || detailRes?.data?.city,
        phone: prev?.phone || detailRes?.data?.phone,
        estimates: prev?.estimates || estimatesRes?.data || [],
      }));
    } catch (err) {
      console.error('Failed to enrich lead for templates:', err);
    }
  }, [lead]);

  useEffect(() => {
    if (isOpen && lead) {
      setMessage('');
      setSubject('');
      setSelectedTemplateId('');
      setStatusMessage('');
      setIsError(false);
      setTemplateLead(lead);
      setSelectedRecipient('');
      loadTemplates();
      loadHistory();
      enrichLeadForTemplates();
    }
  }, [isOpen, lead, loadTemplates, loadHistory, enrichLeadForTemplates]);

  const selectedTemplate = templates.find((t) => String(t.id) === String(selectedTemplateId));

  // Picking a template loads its default text (variables resolved) into the
  // editable textarea — same behavior as the SMS composer. Whatever the rep
  // ends up with is what gets sent, so edits always win.
  const handleTemplateChange = (nextId) => {
    setSelectedTemplateId(nextId);

    if (!nextId) {
      setMessage('');
      setSubject('');
      return;
    }

    const tpl = templates.find((t) => String(t.id) === String(nextId));
    if (!tpl) return;

    const src = templateLead || lead;
    const latestEstimate = src?.estimates?.find((e) => e.publicToken) || src?.estimates?.[0] || null;
    setMessage(tpl.default_body ? replaceTemplateVariables(tpl.default_body, src, latestEstimate) : '');
    setSubject(tpl.subject ? replaceTemplateVariables(tpl.subject, src, latestEstimate) : '');
  };

  const handleSend = async () => {
    setStatusMessage('');
    setIsError(false);

    if (!hasRecipient) {
      setStatusMessage('Error: this lead has no email address on file');
      setIsError(true);
      return;
    }
    if (!selectedTemplateId) {
      setStatusMessage('Error: pick a template');
      setIsError(true);
      return;
    }

    setIsSending(true);
    try {
      await sendEmail({
        leadId: lead.id,
        emailTemplateId: Number(selectedTemplateId),
        message: message.trim() || undefined,
        subject: subject.trim() || undefined,
        // Only sent when the rep actually picked a non-default address, so a
        // single-address lead produces the same request body as before.
        toEmail: selectedRecipient || undefined,
      });

      setStatusMessage(`Email sent to ${recipient}`);
      setIsError(false);
      setMessage('');
      setSubject('');
      // Refresh so the just-sent email appears in the list above.
      loadHistory();
    } catch (error) {
      setStatusMessage(`Error: ${error.response?.data?.message || error.message}`);
      setIsError(true);
    } finally {
      setIsSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    // hideFooter + our own buttons: the shared Modal ignores confirmText and
    // confirmDisabled, so its built-in footer can't express a disabled send.
    <Modal isOpen={isOpen} onCancel={onClose} title={`Email ${lead?.name || 'Lead'}`} hideFooter>
      {statusMessage && <StatusMessage error={isError}>{statusMessage}</StatusMessage>}

      {!hasRecipient ? (
        <BlockedNotice>
          <FaExclamationTriangle />
          <span>
            This lead has no email address on file, so an email can&apos;t be sent.
            Add one by editing the lead first.
          </span>
        </BlockedNotice>
      ) : (
        <>
          {recipientOptions.length > 1 ? (
            <RecipientPicker>
              <RecipientLabel>Send to</RecipientLabel>
              {recipientOptions.map((opt) => {
                const chosen = recipient.toLowerCase() === opt.value.toLowerCase();
                return (
                  <RecipientOption
                    key={opt.value}
                    type="button"
                    $active={chosen}
                    onClick={() => setSelectedRecipient(opt.value)}
                  >
                    <input type="radio" name="emailRecipient" checked={chosen} readOnly />
                    <span className="recipient-text">
                      {opt.value}
                      {opt.isPrimary ? ' (Primary)' : opt.label ? ` (${opt.label})` : ''}
                    </span>
                  </RecipientOption>
                );
              })}
            </RecipientPicker>
          ) : (
            <Recipient>
              <FaEnvelope />
              {recipient}
            </Recipient>
          )}

          {isLoading ? (
            <Loading>
              <FaSpinner className="icon-spin" />
              <p>Loading…</p>
            </Loading>
          ) : (
            history.length > 0 && (
              <HistoryContainer>
                {history.map((item) => (
                  <HistoryItem key={item.id}>
                    <HistorySubject>{item.subject || item.templateName || 'Email'}</HistorySubject>
                    {item.body && <div>{item.body}</div>}
                    <HistoryMeta>
                      {new Date(item.timestamp).toLocaleString()}
                      {item.status ? ` · ${item.status}` : ''}
                    </HistoryMeta>
                  </HistoryItem>
                ))}
              </HistoryContainer>
            )
          )}

          <Field>
            <TemplateSelector
              templates={templates}
              selectedId={selectedTemplateId ? Number(selectedTemplateId) : null}
              onSelect={(tpl) => handleTemplateChange(tpl ? tpl.id : '')}
              label="Template"
              emptyMessage="No email templates yet. Create one in Follow-Ups → Email Templates."
              // Email templates have no body of their own — the HTML lives in
              // Brevo — so preview the default message instead.
              getPreview={(t) => t.default_body || t.description || `Brevo template #${t.brevo_template_id}`}
            />
            {selectedTemplate?.description && <Hint>{selectedTemplate.description}</Hint>}
          </Field>

          <Field>
            <Label htmlFor="email-subject">Subject (optional)</Label>
            <Input
              id="email-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={selectedTemplate?.subject || "Leave blank to use the template's subject"}
              disabled={isSending}
            />
          </Field>

          <Field>
            <Label htmlFor="email-message">Message</Label>
            <TextArea
              id="email-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                selectedTemplateId
                  ? 'Edit the template text, or write your own.'
                  : 'Pick a template to load its default text, or write your own.'
              }
              rows={10}
              disabled={isSending}
            />
            <Hint>
              Line breaks and paragraphs are preserved. Branding and signature come from the
              Brevo template.
            </Hint>
          </Field>
        </>
      )}

      <Actions>
        <Btn type="button" onClick={onClose} disabled={isSending}>
          Cancel
        </Btn>
        <Btn
          type="button"
          variant="primary"
          onClick={handleSend}
          disabled={isSending || !hasRecipient || !selectedTemplateId}
        >
          {isSending ? <FaSpinner className="icon-spin" /> : <FaPaperPlane />}
          {isSending ? 'Sending…' : 'Send Email'}
        </Btn>
      </Actions>
    </Modal>
  );
};

export default EmailModal;
